import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import { useAppDispatch, useAppSelector } from 'modules/state-mgmt/store';
import { fetchCertificationGroups, fetchAllCertifications } from 'modules/state-mgmt/onboarding/actions';
import { getEffectiveDateText, shouldShowDateText, isCertificationExempted } from '../utils/certificationValidation';

interface CertificationItem {
  id: string;
  idNumber: string;
  completionDate: string;
  expirationDate: string;
  createdAt: string;
  lastUpdatedAt: string;
  description: string;
  certification: {
    id: string;
    name: string;
    isExpirationDateMandatory: boolean;
  };
  project: any;
  alias: any;
  aliasesInProjects: any[];
  projectVisibility: number;
}

interface CertificationType {
  id: string;
  name: string;
  isExpirationDateMandatory: boolean;
}

interface TypeOption {
  value: CertificationType;
  label: string;
  group: string;
  effectiveDateText?: string;
}

interface CertificationData {
  exemptedCertifications: CertificationItem[];
  allCertifications: CertificationItem[];
  certificationGroups: any[];
  certificationTypes: any;
  typeOptions: TypeOption[];
  certificationMeta: {
    pageNumber: number;
    pageSize: number;
    totalResults: number;
  } | null;
  isLoading: boolean;
  error: string | null;
  isArchivedProject: boolean;
}

export const useCertifications = (): CertificationData => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { workerId, projectId, languageCode, isArchivedProject, isOffline, isWorker } = useAppSelector(state => ({
    workerId: state.onboarding.id,
    projectId: state.onboarding?.temp?.project?.value,
    languageCode: state.app?.languageCode,
    isArchivedProject: state.cache.project.status === 3,
    isOffline: !state.app.isOnline || state.onboarding?.uploadingDetails?.isOffline,
    isWorker: !state.user?.userId || state.user?.isWorker,
  }));

  const { allCertificationsResponse, certificationGroups, certificationTypes } = useAppSelector(state => ({
    certificationGroups: state.onboarding.certificationGroups,
    allCertificationsResponse: state.onboarding.allCertifications,
    certificationTypes: state.cache.certificationTypes,
  }));

  const exemptedCertifications: CertificationItem[] = [];
  const allCertifications: CertificationItem[] = (allCertificationsResponse as any)?.items || [];

  // Generate type options for the certification modal
  const typeOptions = useMemo((): TypeOption[] => {
    if (!certificationTypes || (!certificationTypes.en?.length && !certificationTypes.es?.length)) {
      return [];
    }

    const allCertTypes = certificationTypes[languageCode] || [];

    if (!allCertTypes.length) {
      return [];
    }

    const availableCertTypes = [...allCertTypes];
    const options: TypeOption[] = [];

    // Process certification groups if available
    if (certificationGroups?.length > 0) {
      certificationGroups.forEach((group: any) => {
        // Create group name with effective date text (same as warning modal)
        const groupDisplayName = group.name + (shouldShowDateText(group) ? ` (${getEffectiveDateText(group, t)})` : '');

        const groupOptions: TypeOption[] = [];

        // Find certifications that belong to this group
        group.certifications.forEach((groupCert: any) => {
          const certTypeIndex = availableCertTypes.findIndex(ct => ct.id === groupCert.id);
          if (certTypeIndex !== -1) {
            const certType = availableCertTypes[certTypeIndex];

            // Use alias if available, otherwise use name
            const displayName = groupCert.alias || groupCert.name || certType.name;

            // Check if this certification is exempted
            const isExempted = isCertificationExempted(groupCert.id, certificationGroups || []);
            const finalDisplayName = isExempted
              ? `${displayName} (${t('certifications.addModal.exempted')})`
              : displayName;

            groupOptions.push({
              value: certType,
              label: finalDisplayName,
              group: groupDisplayName,
              effectiveDateText: shouldShowDateText(group) ? getEffectiveDateText(group, t) : undefined,
            });

            // Remove from available types so it doesn't appear in "Other"
            availableCertTypes.splice(certTypeIndex, 1);
          }
        });

        groupOptions.sort((a, b) => a.label.localeCompare(b.label));
        options.push(...groupOptions);
      });
    }

    // Add remaining certifications to "Other" group
    if (availableCertTypes.length > 0) {
      const otherOptions = availableCertTypes
        .map(certType => ({
          value: certType,
          label: certType.name,
          group: t('certifications.addModal.otherGroup'),
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

      options.push(...otherOptions);
    }

    return options;
  }, [certificationGroups, certificationTypes, languageCode, t]);

  const certificationMeta = (allCertificationsResponse as any)
    ? {
        pageNumber: (allCertificationsResponse as any).pageNumber || 1,
        pageSize: (allCertificationsResponse as any).pageSize || 0,
        totalResults: (allCertificationsResponse as any).totalResults || 0,
      }
    : null;

  useEffect(() => {
    const loadCertificationData = async () => {
      if (!workerId || !projectId) return;
      if (isOffline && !isWorker) return;

      setIsLoading(true);
      setError(null);

      try {
        const promises = [
          dispatch(fetchCertificationGroups({ projectId, workerId, languageCode })),
          dispatch(fetchAllCertifications({ workerId, languageCode })),
        ];

        await Promise.all(promises);
      } catch (err) {
        const errorMessage = t('certifications.errors.failedToLoadData', 'Failed to load certification data');
        setError(errorMessage);
        enqueueSnackbar(errorMessage, { variant: 'error' });
      } finally {
        setIsLoading(false);
      }
    };

    loadCertificationData();
  }, [dispatch, workerId, projectId, languageCode, t]);

  return {
    exemptedCertifications,
    allCertifications,
    certificationGroups,
    certificationTypes: certificationTypes || { en: [], es: [] },
    typeOptions,
    certificationMeta,
    isLoading,
    error,
    isArchivedProject,
  };
};
